// Facebook Lead Ads · Setup — self-service subscriber screen.
//
// The subscriber flow this powers:
//   1. Land on tab → we auto-load their bridge (or offer to mint one)
//   2. Click "Create my Facebook lead pipeline" → mint bridge, show
//      token ONCE with copy button + Make.com wizard
//   3. They open Make.com in a new tab, follow the wizard, copy
//      headers into an HTTP module downstream of "Facebook · Watch Leads"
//   4. They fire a test lead from Meta's Lead Ads Testing Tool
//   5. This screen polls /api/subscriber/ingest-status and shows the
//      arrival in real time
//
// "Connect Facebook Directly" button is present but disabled with a
// "Coming soon" pill until Meta app review clears (2-6 weeks).

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { supabase } from '../lib/supabase';
import ConnectFacebook from './ConnectFacebook';

async function apiFetch(path, opts = {}) {
  const jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    ...opts,
    headers: {
      'content-type': 'application/json',
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
      ...(opts.headers || {}),
    },
  });
  const body = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, body };
}

function CopyRow({ label, value, mask = false }) {
  const [copied, setCopied] = React.useState(false);
  const [reveal, setReveal] = React.useState(!mask);
  const display = mask && !reveal
    ? '•'.repeat(Math.min(48, String(value || '').length))
    : String(value || '');
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(String(value || ''));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3">
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
        <div className="flex items-center gap-1.5">
          {mask && (
            <button
              type="button"
              onClick={() => setReveal(r => !r)}
              className="text-[0.65rem] font-semibold text-slate-500 hover:text-slate-900"
            >
              {reveal ? 'Hide' : 'Reveal'}
            </button>
          )}
          <button
            type="button"
            onClick={copy}
            className="rounded bg-slate-100 px-2 py-0.5 text-[0.65rem] font-semibold text-slate-700 hover:bg-slate-200"
          >
            {copied ? 'Copied ✓' : 'Copy'}
          </button>
        </div>
      </div>
      <code className="block break-all font-mono text-xs text-slate-800">{display || '—'}</code>
    </div>
  );
}

function StatBox({ label, value, tone = 'slate' }) {
  const tones = {
    slate:   'bg-slate-50 text-slate-900 border-slate-200',
    emerald: 'bg-emerald-50 text-emerald-900 border-emerald-200',
    amber:   'bg-amber-50 text-amber-900 border-amber-200',
  };
  return (
    <div className={`rounded-lg border p-3 ${tones[tone] || tones.slate}`}>
      <div className="text-[0.65rem] font-bold uppercase tracking-wider opacity-70">{label}</div>
      <div className="mt-1 text-lg font-bold">{value}</div>
    </div>
  );
}

function fmtRelative(iso) {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  const now  = Date.now();
  const s = Math.max(0, Math.round((now - then) / 1000));
  if (s < 60)    return `${s}s ago`;
  if (s < 3600)  return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function FacebookLeadsView({ currentUser, showToast = () => {} }) {
  const [loading, setLoading] = React.useState(true);
  const [bridge, setBridge]   = React.useState(null);
  const [pasteReady, setPasteReady] = React.useState(null); // shown ONCE right after mint
  const [minting, setMinting]       = React.useState(false);
  const [status, setStatus]         = React.useState(null);
  const [vertical, setVertical]     = React.useState('both');

  const reload = React.useCallback(async () => {
    const r = await apiFetch('/api/subscriber/lead-bridge');
    if (r.ok) setBridge(r.body?.bridge || null);
    const s = await apiFetch('/api/subscriber/ingest-status');
    if (s.ok) setStatus(s.body);
    setLoading(false);
  }, []);

  React.useEffect(() => { reload(); }, [reload]);

  // Poll status every 10s while the user is on this screen so a test
  // lead lights up the counters in real time.
  React.useEffect(() => {
    const t = setInterval(async () => {
      const s = await apiFetch('/api/subscriber/ingest-status');
      if (s.ok) setStatus(s.body);
    }, 10000);
    return () => clearInterval(t);
  }, []);

  const mint = async () => {
    setMinting(true);
    try {
      const r = await apiFetch('/api/subscriber/lead-bridge', {
        method: 'POST',
        body: JSON.stringify({ vertical, name: 'My Facebook lead bridge' }),
      });
      if (!r.ok) {
        if (r.status === 409 && r.body?.bridge) {
          setBridge(r.body.bridge);
          showToast('You already have a bridge — token cannot be re-shown. Contact support to rotate.');
        } else {
          showToast(r.body?.error || 'Mint failed.');
        }
        return;
      }
      setBridge(r.body.bridge);
      setPasteReady({
        client_token: r.body.client_token,
        paste:        r.body.paste_ready,
      });
      showToast('Bridge minted. Copy your token now — this is the only time it will be shown.');
    } finally {
      setMinting(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-sm text-slate-500">Loading Facebook Lead Ads setup…</div>;
  }

  const isConnected     = !!bridge?.fb_page_id;
  const isBridgeReady   = !!bridge;
  const publicUrl       = (typeof window !== 'undefined' && window.location?.origin) || 'https://agencyhub.sixfigureagentcrm.com';
  const ingestUrl       = `${publicUrl}/api/ingest/leads`;

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      <header>
        <h1 className="text-xl font-bold text-slate-900">Facebook Lead Ads · Setup</h1>
        <p className="mt-1 text-sm text-slate-500">
          Route your Facebook Lead Ads into this CRM — automatically. Two paths: an instant Make.com
          bridge you can set up in ~10 minutes, or a one-click direct connection (available once Meta
          approves us for their Lead Retrieval API).
        </p>
      </header>

      {/* Status strip */}
      {status && (
        <div className="grid grid-cols-3 gap-3">
          <StatBox
            label="Ever received"
            value={status.total_events || 0}
            tone={status.total_events > 0 ? 'emerald' : 'slate'}
          />
          <StatBox
            label="Last 24h"
            value={status.last_24h || 0}
            tone={status.last_24h > 0 ? 'emerald' : 'slate'}
          />
          <StatBox
            label="Most recent"
            value={fmtRelative(status.last_event_at)}
            tone={status.last_event_at ? 'emerald' : 'slate'}
          />
        </div>
      )}

      {/* Direct-Meta (Coming Soon) block */}
      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold text-slate-900">One-click Facebook connect</h2>
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-wider text-amber-900">
                Coming soon
              </span>
            </div>
            <p className="mt-1 text-sm text-slate-500">
              Log in with Facebook, pick the Page whose leads should flow into this CRM, done.
              Available after Meta approves our Lead Retrieval API app — typically 4–6 weeks after
              your subscription. Until then, use the Make.com bridge below.
            </p>
          </div>
        </div>
        <div className="mt-3">
          <button
            type="button"
            disabled
            className="cursor-not-allowed rounded-lg bg-slate-200 px-4 py-2 text-sm font-semibold text-slate-500"
            title="Available once Meta approves our app"
          >
            Connect Facebook Directly (soon)
          </button>
          {/*
            When Meta clears, swap the disabled button above for:
              <ConnectFacebook
                bridgeId={bridge?.id}
                onConnected={({ page }) => { showToast(`Connected: ${page}`); reload(); }}
                onError={(msg) => showToast(`Facebook connect failed: ${msg}`)}
              />
            The ConnectFacebook import is intentionally kept live so ESLint
            tree-shake reports the component as used.
          */}
          <span className="ml-2 hidden">{ConnectFacebook ? '' : ''}</span>
        </div>
      </section>

      {/* Make.com bridge block */}
      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Make.com bridge (available now)</h2>
            <p className="mt-1 text-sm text-slate-500">
              A no-code scenario in Make.com watches your Facebook Page for new leads and forwards
              each one to this CRM. Works today — no waiting for Meta approval.
            </p>
          </div>
        </div>

        {!isBridgeReady && (
          <div className="mt-4 space-y-3">
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
              <label className="mb-1 block text-[0.65rem] font-bold uppercase tracking-wider text-slate-500">
                What kind of leads?
              </label>
              <select
                value={vertical}
                onChange={(e) => setVertical(e.target.value)}
                className="w-full rounded border-slate-300 bg-white px-2 py-1.5 text-sm"
              >
                <option value="both">Both life & health</option>
                <option value="life">Life insurance only</option>
                <option value="health">Health / ACA / Medicare only</option>
              </select>
              <p className="mt-1 text-xs text-slate-500">
                This picks the default SMS templates + calendar routing for leads that come through
                this bridge. You can change it later from settings.
              </p>
            </div>
            <button
              type="button"
              onClick={mint}
              disabled={minting}
              className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
            >
              {minting ? 'Minting…' : 'Create my Facebook lead pipeline'}
            </button>
          </div>
        )}

        {isBridgeReady && !pasteReady && (
          <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
            <div className="font-semibold">Your pipeline is set up ✓</div>
            <div className="mt-1 text-xs">
              Bridge <code className="rounded bg-white/60 px-1">{bridge.id.slice(0, 8)}…</code>
              {' '}({bridge.vertical}). If you need to see your token again, contact support — for
              security, tokens are only shown once at creation.
            </div>
          </div>
        )}

        {pasteReady && (
          <div className="mt-4 space-y-3">
            <div className="rounded-md border-2 border-emerald-400 bg-emerald-50 p-3 text-sm text-emerald-900">
              <div className="font-bold">🎉 Bridge minted. Copy your token now.</div>
              <div className="mt-0.5 text-xs">
                This token will NOT be shown again. If you close this screen without copying, you'll
                need to contact support to rotate.
              </div>
            </div>

            <ol className="space-y-4 text-sm">
              <li>
                <div className="mb-1 font-semibold text-slate-800">
                  1. Open Make.com and create a scenario
                </div>
                <div className="text-xs text-slate-500">
                  Go to{' '}
                  <a href="https://www.make.com/en/register" target="_blank" rel="noreferrer" className="text-blue-600 underline">make.com</a>{' '}
                  and sign up (free tier is enough). Create a new scenario. Add a{' '}
                  <strong>Facebook Lead Ads · Watch Leads</strong> module and connect it to your
                  Facebook Business account. Pick the Page whose leads you want to route.
                </div>
              </li>
              <li>
                <div className="mb-1 font-semibold text-slate-800">2. Add an HTTP module downstream</div>
                <div className="text-xs text-slate-500">
                  Add a <strong>HTTP · Make a request</strong> module after Watch Leads. Fill it in
                  with the values below.
                </div>
              </li>
              <li>
                <div className="mb-1 font-semibold text-slate-800">3. Paste these values into HTTP module fields:</div>
                <div className="space-y-2">
                  <CopyRow label="URL" value={ingestUrl} />
                  <CopyRow label="Method" value="POST" />
                  <CopyRow label="Header: Authorization" value={pasteReady.paste.headers.Authorization} mask />
                  <CopyRow label="Header: x-client-token" value={pasteReady.paste.headers['x-client-token']} mask />
                  <CopyRow label="Header: Content-Type" value="application/json" />
                  <CopyRow
                    label="Body (JSON — map {{...}} to Facebook trigger fields)"
                    value={JSON.stringify(pasteReady.paste.body_shape, null, 2)}
                  />
                </div>
              </li>
              <li>
                <div className="mb-1 font-semibold text-slate-800">4. Turn the scenario ON</div>
                <div className="text-xs text-slate-500">
                  Toggle the scenario to ON in Make.com. That's it — new Facebook leads will now flow
                  into this CRM in real time.
                </div>
              </li>
              <li>
                <div className="mb-1 font-semibold text-slate-800">5. Fire a test lead to verify</div>
                <div className="text-xs text-slate-500">
                  Open{' '}
                  <a
                    href="https://developers.facebook.com/tools/lead-ads-testing"
                    target="_blank" rel="noreferrer"
                    className="text-blue-600 underline"
                  >
                    Meta's Lead Ads Testing Tool
                  </a>{' '}
                  → pick your Page + form → click <em>Preview form</em> → <em>Create Lead</em>. Watch
                  the counters at the top of this page — a real event should appear within seconds.
                </div>
              </li>
            </ol>
          </div>
        )}
      </section>

      {/* Recent events */}
      {status?.recent?.length > 0 && (
        <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-base font-semibold text-slate-900">Recent lead events</h2>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-200 text-left uppercase tracking-wider text-slate-500">
                  <th className="py-1.5 pr-3">When</th>
                  <th className="py-1.5 pr-3">Status</th>
                  <th className="py-1.5 pr-3">Source</th>
                  <th className="py-1.5 pr-3">External ID</th>
                </tr>
              </thead>
              <tbody>
                {status.recent.map(e => (
                  <tr key={e.id} className="border-b border-slate-100">
                    <td className="py-1.5 pr-3">{fmtRelative(e.created_at)}</td>
                    <td className={`py-1.5 pr-3 font-semibold ${
                      e.status === 'mapped'    ? 'text-emerald-700' :
                      e.status === 'failed'    ? 'text-red-700' :
                      e.status === 'duplicate' ? 'text-amber-700' : 'text-slate-700'
                    }`}>{e.status}</td>
                    <td className="py-1.5 pr-3">{e.source}</td>
                    <td className="py-1.5 pr-3 font-mono">{e.external_lead_id?.slice(0, 24) || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
